/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * timeTrackerFormat.js
 *
 * Pure presentation logic shared by the Time Tracker screen and its tests: how a
 * contract-C1 timer object turns into a state word, a number of seconds and a
 * clock string.
 *
 * These are deliberate ports of `app/widgets/WidgetViews.js`'s private
 * `resolveTimerState` / `resolveTimerSeconds` / `formatClock`, so the in-app
 * screen and the home-screen widget can never show two different numbers for the
 * same timer. `WidgetViews` cannot import from here: it is rasterized by
 * `react-native-android-widget` inside a headless task and its module graph is
 * kept deliberately tiny.
 *
 * Imports nothing — usable from any renderer the app ships (native, web, Tauri).
 *
 * @module helpers/timeTrackerFormat
 */

/** Segment types the backend counts as a tracked break. */
const BREAK_SEGMENT_TYPES = ['BREAK', 'COFFEE_TIME'];

/** The states the widget timer facade publishes (contract C1). */
const TIMER_STATES = ['running', 'break', 'paused'];

/**
 * Resolve the authoritative widget state. Prefers the backend's own
 * `timer.state`; falls back to the pre-C1 heuristics so an older payload keeps
 * rendering sensibly.
 * @param {object|null} timer Timer object from `/widgets/summary` or a facade call.
 * @returns {'running'|'break'|'paused'|'idle'} The resolved state.
 */
export const resolveTrackerState = (timer) => {
    if (!timer || !timer.id) {
        return 'idle';
    }
    if (TIMER_STATES.includes(timer.state)) {
        return timer.state;
    }
    if (BREAK_SEGMENT_TYPES.includes(String(timer.segmentType || '').toUpperCase())) {
        return 'break';
    }

    return timer.startedAt ? 'running' : 'paused';
};

/**
 * Seconds to put on the big clock.
 *
 * Running / on break → elapsed of the CURRENT segment. Paused →
 * `pausedElapsedSeconds`: the time actually tracked in the row that was paused.
 * `startedAt` is null while paused, so `now - new Date(null)` would yield
 * garbage — and `workSeconds` is just as wrong there: it is `null` for a project
 * timer (00:00:00 on every paused project) and the work order's LIFETIME total
 * otherwise, which would make a five-minute session jump to 14:00:00 the instant
 * Pause was pressed. Falls back to 0, NEVER to `workSeconds`.
 *
 * @param {object|null} timer Timer object.
 * @param {number} [nowMs] Reference timestamp in ms (injectable for tests).
 * @returns {number} Non-negative whole seconds.
 */
export const resolveTrackerElapsedSeconds = (timer, nowMs = Date.now()) => {
    const state = resolveTrackerState(timer);

    if (state === 'idle') {
        return 0;
    }
    if (state === 'paused') {
        return Math.max(0, Math.floor(Number(timer?.pausedElapsedSeconds) || 0));
    }
    if (!timer?.startedAt) {
        return Math.max(0, Math.floor(Number(timer?.workSeconds) || 0));
    }

    const started = new Date(timer.startedAt).getTime();
    if (Number.isNaN(started)) {
        return 0;
    }

    return Math.max(0, Math.floor((nowMs - started) / 1000));
};

/**
 * `HH:MM:SS`, zero-padded and never negative. The screen can afford real seconds
 * (unlike the Android widget, whose RemoteViews cannot self-tick).
 * @param {number} seconds Elapsed seconds.
 * @returns {string} The clock string.
 */
export const formatTrackerClock = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const parts = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];

    return parts.map((part) => String(part).padStart(2, '0')).join(':');
};

/**
 * Compact `Xó Yp` duration for totals and list rows. Under a minute it degrades
 * to `Xp` rather than rendering a bare "0ó 0p" for real tracked work.
 *
 * The unit suffixes are INJECTED rather than hardcoded: this feeds a fully
 * localized screen, so a German user reading six translated strings must not
 * then be shown "3ó 20p". The Hungarian defaults keep the widget's own
 * `formatHM` twin (which has no catalog to read) identical.
 *
 * @param {number} seconds Elapsed seconds.
 * @param {{hour?: string, minute?: string}} [units] Localized unit suffixes.
 * @returns {string} The human duration.
 */
export const formatTrackerDuration = (seconds, units = {}) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const hourUnit = units.hour || 'ó';
    const minuteUnit = units.minute || 'p';

    if (hours > 0) {
        return `${hours}${hourUnit} ${minutes}${minuteUnit}`;
    }

    return `${Math.max(minutes, total > 0 ? 1 : 0)}${minuteUnit}`;
};

/**
 * The headline / eyebrow pair for a timer or a quick-start candidate: the TASK is
 * the bold primary line and its parent the small eyebrow, with the eyebrow
 * dropped entirely when it would only repeat the headline.
 *
 * The parent is `projectTitle` for a timer and `title` for a trackable (whose
 * `title` IS its work order / project, while its `subtitle` is the customer) —
 * one expression covers both because only one of the two fields is ever set.
 * A row with no task falls back to its own title, with the customer as the
 * second line. Mirrors `WidgetTimer.headline`/`.eyebrow` and
 * `WidgetTrackable.headline`/`.caption` in
 * `targets/widgets/WidgetSharedData.swift`, and `trackableHeadline` /
 * `trackableCaption` in `app/widgets/WidgetViews.js`.
 *
 * @param {object|null} item Timer or trackable.
 * @returns {{headline: string, eyebrow: string}} Never null, possibly empty.
 */
export const resolveTrackerLabels = (item) => {
    const taskTitle = String(item?.taskTitle || '').trim();
    const title = String(item?.title || '').trim();
    const projectTitle = String(item?.projectTitle || '').trim();
    const subtitle = String(item?.subtitle || '').trim();

    if (taskTitle) {
        const parent = projectTitle || title;

        return {headline: taskTitle, eyebrow: parent && parent !== taskTitle ? parent : ''};
    }

    const headline = title || projectTitle;

    return {headline, eyebrow: subtitle && subtitle !== headline ? subtitle : ''};
};

export default {
    resolveTrackerState,
    resolveTrackerElapsedSeconds,
    formatTrackerClock,
    formatTrackerDuration,
    resolveTrackerLabels,
};
